import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { KollelFolderDtoIn, RejectKollelFolderDtoIn } from '@/models/in/KollelFolderDtoIn';
import { formatCurrencyShort } from '@/helpers/currencyHelper';

interface KollelFolderApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  folder: KollelFolderDtoIn;
  onSuccess: () => void;
}

/**
 * Reviews one kollel monthly request folder (HAT-1446 phase 6): shows every included avrech's
 * stipend breakdown, and lets the office approve or reject. Approving immediately creates a real
 * SupportRequest per item (KollelsService.ApproveFolderAsync) - there is no "undo", which is why
 * the primary action is phrased as a commitment, not a draft save.
 */
const KollelFolderApprovalDialog = ({ open, onClose, folder, onSuccess }: KollelFolderApprovalDialogProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const approveMutation = usePutAuthApi<KollelFolderDtoIn>(`management/kollels/folders/${folder.id}/approve`);
  const rejectMutation = usePutAuthApi<KollelFolderDtoIn, object, RejectKollelFolderDtoIn>(
    `management/kollels/folders/${folder.id}/reject`,
  );

  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const total = folder.items.reduce((sum, item) => sum + item.totalAmount, 0);

  const handleApprove = async () => {
    setError(null);
    try {
      await approveMutation.mutateAsync({});
      onSuccess();
      onClose();
    } catch {
      setError(t('kollelFoldersManagement.approvalDialog.approveError'));
    }
  };

  const handleReject = async () => {
    setError(null);
    try {
      await rejectMutation.mutateAsync({ body: { comment: comment.trim() || null } });
      onSuccess();
      onClose();
    } catch {
      setError(t('kollelFoldersManagement.approvalDialog.rejectError'));
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('kollelFoldersManagement.approvalDialog.title', { name: folder.kollelName, month: folder.requestedForMonth })}
    >
      <div className="flex flex-col gap-4 p-4 max-h-[60vh] overflow-y-auto">
        {folder.allowedNextStatuses.length === 0 && (
          <p className="body-sm-regular text-(--font-secondary)">{t('kollelFoldersManagement.approvalDialog.alreadyDecided')}</p>
        )}

        <div className="flex flex-col gap-2">
          {folder.items.map((item) => (
            <div key={item.supportedId} className="flex items-center justify-between border border-(--border-main) rounded-lg p-3">
              <span className="body-sm-semibold">{`${item.firstName ?? ''} ${item.lastName ?? ''}`.trim()}</span>
              <span className="body-sm-regular">{formatCurrencyShort(item.totalAmount)}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-(--border-main) pt-3">
          <span className="body-sm-semibold">{t('kollelFoldersManagement.approvalDialog.total')}</span>
          <span className="body-sm-semibold">{formatCurrencyShort(total)}</span>
        </div>

        {folder.allowedNextStatuses.length > 0 && (
          <label className="flex flex-col gap-1">
            <span className="body-sm-semibold text-(--font-secondary)">{t('kollelFoldersManagement.approvalDialog.commentLabel')}</span>
            <textarea
              rows={3}
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('kollelFoldersManagement.approvalDialog.commentPlaceholder')}
            />
          </label>
        )}

        {error && <p className="body-sm-regular text-(--color-danger)">{error}</p>}
      </div>

      <div className="flex items-center justify-between p-4 border-t border-(--border-main)">
        <Button variant="white" onClick={onClose} disabled={isPending}>
          {t('close')}
        </Button>
        {folder.allowedNextStatuses.length > 0 && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleReject} disabled={isPending}>
              {t('kollelFoldersManagement.approvalDialog.reject')}
            </Button>
            <Button variant="primary" onClick={handleApprove} disabled={isPending}>
              {t('kollelFoldersManagement.approvalDialog.approve')}
            </Button>
          </div>
        )}
      </div>
    </MainDialogLayout>
  );
};

export default KollelFolderApprovalDialog;
